import { useState } from 'react';
import { Card, Typography, Table, Tag, Button, Space, Modal, Textarea, Descriptions, Select, Row, Col, Statistic, Tooltip, StatCard, EntityAvatar, message } from '../../ui';
import { CheckCircle2, XCircle, Percent, IndianRupee, Hourglass, FileText } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { useFocusRow } from '../../hooks/useFocusRow';
import { usePermission } from '../../store/auth';
import { formatIstDate } from '../../lib/time';
import { inr2 } from '../../lib/format';
import { RoleBadge } from '../../components/RoleBadge';
import { useServerSort } from '../../hooks/useServerSort';

const STATUS_COLOR: Record<string, 'gold' | 'green' | 'red'> = { PENDING: 'gold', APPROVED: 'green', REJECTED: 'red' };

/**
 * After-billing prompt-payment Cash Discount — approval queue. Proposals arrive PENDING; an approver
 * (Accounts/Admin/SA) posts the ledger credit only with mandatory audit remarks. The Tally invoice is
 * never modified.
 */
export function DiscountAdjustmentsPage() {
  // A link naming one record (?focus=) opens on every status: a decided adjustment is not in the Pending default, so
  // "Discount adjustment approved" landed on a list without the row it was about (E2E 15 Sep, DA/2026/00015).
  const [status, setStatus] = useState<string>(() => (new URLSearchParams(window.location.search).get('focus') ? '' : 'PENDING'));
  const [approveFor, setApproveFor] = useState<any>(null);
  const [rejectFor, setRejectFor] = useState<any>(null);
  const canApprove = usePermission('DISCOUNT.ADJUST.APPROVE');
  const qc = useQueryClient();

  // Server-side sort: the list is capped at 500 and the dealer / invoice / raised-by names are
  // resolved server-side, so only the backend can order the whole set by them.
  const { sortState, onSortChange, sort } = useServerSort();

  const { data, isLoading } = useQuery({
    queryKey: ['discount-adjustments', status, sort],
    queryFn: () => request<any[]>({ url: '/discount-adjustments', params: { ...(status ? { status } : {}), ...(sort ? { sort } : {}) } }),
  });
  const rows = data ?? [];
  const pendingAmt = rows.filter((r: any) => r.status === 'PENDING').reduce((s: number, r: any) => s + Number(r.amount || 0), 0);

  // A deep-link from the approvals hub / a notification names the record it meant (?focus=…);
  // open that row instead of dropping the reader at the top of the list to hunt for it.
  const focus = useFocusRow(rows, { match: ['id', 'adjustmentNo'] });

  const columns = [
    { title: 'Adj No', dataIndex: 'adjNo', sorter: true },
    {
      title: 'Dealer', dataIndex: 'retailerName', width: 230, sorter: true,
      render: (v: any, r: any) => (
        // No mark: it was a Store glyph repeated on every row — a constant glyph is not identity
        // (see the colour-sweep brief).
        <span className="block min-w-0 truncate" title={v ?? undefined}>{v ?? `#${r.retailerId}`}</span>
      ),
    },
    {
      title: 'Invoice', dataIndex: 'invoiceNo', sorter: true,
      render: (v: any) => (v
        ? <span className="inline-flex min-w-0 items-center gap-1.5 type-label text-content"><FileText size={12} className="shrink-0 text-subtle" /><span className="truncate">{v}</span></span>
        : <span className="text-subtle">—</span>),
    },
    { title: 'Basis', dataIndex: 'basisAmount', align: 'right' as const, sorter: true, render: (v: any) => inr2(v) },
    {
      title: '%', dataIndex: 'pct', align: 'right' as const, sorter: true,
      render: (v: any) => (
        <span className="inline-flex items-center rounded-pill border border-sky-500/30 bg-sky-500/10 px-2 py-px type-label tabular-nums text-sky-600 dark:text-sky-400">
          {Number(v)}%
        </span>
      ),
    },
    // The figure the decision is actually about — given the weight to match.
    { title: 'Amount', dataIndex: 'amount', align: 'right' as const, sorter: true, render: (v: any) => <b className="tabular-nums text-content">{inr2(v)}</b> },
    {
      // Days-from-invoice is the whole basis of a prompt-payment discount, so it is coloured by how
      // prompt it actually was rather than printed as a neutral chip.
      title: 'Paid on', dataIndex: 'paymentDate', sorter: true,
      render: (v: any, r: any) => (v
        ? (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <span className="whitespace-nowrap text-content">{formatIstDate(v)}</span>
            <Tag tone={Number(r.daysFromInvoice) <= 7 ? 'green' : Number(r.daysFromInvoice) <= 21 ? 'gold' : 'red'}>
              {r.daysFromInvoice}d
            </Tag>
          </span>
        )
        : <span className="text-subtle">—</span>),
    },
    { title: 'Status', dataIndex: 'status', sorter: true, render: (v: string, r: any) => <Space size={4}><Tag tone={STATUS_COLOR[v]}>{v}</Tag>{r.isOverride ? <Tag tone="red">OVERRIDE</Tag> : null}</Space> },
    {
      title: 'By', dataIndex: 'createdByName', width: 190, sorter: true,
      render: (v: any, r: any) => {
        const approved = r.status === 'APPROVED';
        const name = approved ? (r.approvedByName ?? '') : v;
        if (!name) return <span className="text-subtle">—</span>;
        return (
          <span className="flex min-w-0 items-center gap-2">
            <EntityAvatar name={name} size={24} />
            <span className="min-w-0">
              <span className="block truncate text-content" title={name}>{name}</span>
              <span className="block type-caption text-subtle">{approved ? 'approved it' : 'raised it'}</span>
            </span>
          </span>
        );
      },
    },
    ...(canApprove ? [{
      title: '', width: 150,
      render: (_: any, r: any) => r.status === 'PENDING' ? (
        <Space wrap size={4} onClick={(e: any) => e.stopPropagation()}>
          <Tooltip label="Approve adjustment">
            <Button size="sm" variant="primary" aria-label="Approve adjustment" icon={<CheckCircle2 size={16} />} onClick={() => setApproveFor(r)} />
          </Tooltip>
          <Tooltip label="Reject adjustment">
            <Button size="sm" variant="soft-danger" aria-label="Reject adjustment" icon={<XCircle size={16} />} onClick={() => setRejectFor(r)} />
          </Tooltip>
        </Space>
      ) : null,
    }] : []),
  ];

  return (
    <div>
      <Typography.Title level={4}>Discount Adjustments</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        After-billing prompt-payment cash discount. Posts an internal ledger credit only after approval with audit remarks — the tax invoice is never changed.
      </Typography.Paragraph>
{/* Money leads: what is at stake is the reason to open this page, and the count of proposals
          is what it is made of. Not clickable — the Status select right below already IS that filter,
          and a tile that duplicates a visible control adds a second place for the state to disagree. */}
      <Row align="stretch" gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={24} md={8}>
          <StatCard
            tone={pendingAmt ? 'gold' : 'neutral'} icon={<IndianRupee size={18} />}
            label="Awaiting a decision" value={inr2(pendingAmt)}
            hint={`${rows.filter((r: any) => r.status === 'PENDING').length} proposal${rows.filter((r: any) => r.status === 'PENDING').length === 1 ? '' : 's'} pending`}
          />
        </Col>
      </Row>
      <Card>
        <Space style={{ marginBottom: 12 }}>
          <span>Status:</span>
          <Select value={status} onChange={setStatus} className="w-40"
            options={[{ value: '', label: 'All' }, { value: 'PENDING', label: 'Pending' }, { value: 'APPROVED', label: 'Approved' }, { value: 'REJECTED', label: 'Rejected' }]} />
        </Space>
        <Table rowKey="id" loading={isLoading} dataSource={rows} columns={columns as any} size="small" pagination={{ pageSize: 25 }}
          sortState={sortState} onSortChange={onSortChange}
          rowClassName={focus.rowClassName}
          expandable={{
            ...focus.expandable,
            expandedRowRender: (r: any) => (
              <Descriptions size="small" column={2} bordered>
                <Descriptions.Item label="Rule / calculation" span={2}>{r.ruleSource ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="Remarks" span={2}>{r.remarks ?? (r.status === 'REJECTED' ? r.rejectedReason : '—')}</Descriptions.Item>
                <Descriptions.Item label="Ledger ref">{r.ledgerRefId ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="Scheme">#{r.schemeId ?? '—'}</Descriptions.Item>
              </Descriptions>
            ),
          }} />
      </Card>

      <ApproveModal adj={approveFor} onClose={() => setApproveFor(null)} onDone={() => qc.invalidateQueries({ queryKey: ['discount-adjustments'] })} />
      <RejectModal adj={rejectFor} onClose={() => setRejectFor(null)} onDone={() => qc.invalidateQueries({ queryKey: ['discount-adjustments'] })} />
    </div>
  );
}

function ApproveModal({ adj, onClose, onDone }: { adj: any; onClose: () => void; onDone: () => void }) {
  const [remarks, setRemarks] = useState('');
  const m = useConfirmedMutation({ title: "Approve this discount and post the credit?", content: "The credit goes onto the retailer’s ledger straight away.", okText: "Yes, approve" }, {
    mutationFn: () => request({ url: `/discount-adjustments/${adj.id}/approve`, method: 'POST', data: { remarks } }),
    onSuccess: () => { message.success('Approved — ledger credit posted'); setRemarks(''); onDone(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  return (
    <Modal open={!!adj} onOpenChange={(o) => { if (!o) { setRemarks(''); onClose(); } }} title={adj ? `Approve ${adj.adjNo}` : ''}
      footer={<><Button variant="default" onClick={() => { setRemarks(''); onClose(); }}>Cancel</Button><Button variant="primary" disabled={remarks.trim().length < 3} loading={m.isPending} onClick={() => m.mutate()}>Approve & post credit</Button></>}>
      {adj && (
        <>
          <Descriptions size="small" column={1} bordered className="mb-3">
            <Descriptions.Item label="Dealer">{adj.retailerName ?? `#${adj.retailerId}`}</Descriptions.Item>
            <Descriptions.Item label="Invoice">{adj.invoiceNo ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Discount">{Number(adj.pct)}% of {inr2(adj.basisAmount)} = <b>{inr2(adj.amount)}</b></Descriptions.Item>
            <Descriptions.Item label="Basis">Paid {adj.daysFromInvoice}d after invoice → {adj.ruleSource}</Descriptions.Item>
          </Descriptions>
          <Typography.Text type="secondary">This posts a ledger credit of <b>{inr2(adj.amount)}</b> reducing the dealer's outstanding. The tax invoice is not changed.</Typography.Text>
          <Textarea rows={3} maxLength={500} style={{ marginTop: 10 }} placeholder="Audit remarks (required) — e.g. verified UPI UTR, payment within 15 days" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </>
      )}
    </Modal>
  );
}

function RejectModal({ adj, onClose, onDone }: { adj: any; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const m = useConfirmedMutation({ title: "Reject this discount adjustment?", content: "No credit is posted. Your reason goes back to whoever raised it.", okText: "Yes, reject", danger: true }, {
    mutationFn: () => request({ url: `/discount-adjustments/${adj.id}/reject`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Rejected'); setReason(''); onDone(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  return (
    <Modal open={!!adj} onOpenChange={(o) => { if (!o) { setReason(''); onClose(); } }} title={adj ? `Reject ${adj.adjNo}` : ''}
      footer={<><Button variant="default" onClick={() => { setReason(''); onClose(); }}>Cancel</Button><Button variant="danger" disabled={reason.trim().length < 3} loading={m.isPending} onClick={() => m.mutate()}>Reject</Button></>}>
      <Textarea rows={3} maxLength={255} placeholder="Reason for rejection" value={reason} onChange={(e) => setReason(e.target.value)} />
    </Modal>
  );
}
